import { useTranslation } from 'react-i18next'
import { WebCitation } from '@digital-twins/shared-types'

import { CitationSource, hostnameFromUrl, httpsImageSrc, SourceRow } from '@/components/citations'
import { Heading } from '@/components/ui/Heading'

import { formatPublishedDate, groupWebCitationsByUrl } from './ThreadWebSourceCard'

interface ThreadWebSourcesListProps {
  citations: Array<WebCitation>
}

export function webCitationSource(citation: WebCitation, index: number): CitationSource {
  const hostname = hostnameFromUrl(citation.url)
  const publisher = citation.publisher?.trim() || null
  const date = formatPublishedDate(citation.publishedAt)
  const location = [hostname ?? publisher, date].filter((part): part is string => Boolean(part)).join(' · ')
  // Only text the source page provided; often absent.
  const rawSnippet = (citation as WebCitation & { snippet?: unknown }).snippet
  const quote = typeof rawSnippet === 'string' && rawSnippet.trim() ? rawSnippet.trim() : null
  // The page's own image (og:image), when the web evidence carried one; https only.
  const imageSrc = httpsImageSrc((citation as WebCitation & { imageUrl?: unknown }).imageUrl)
  return {
    key: `web:${citation.url}`,
    displayIndex: index + 1,
    kind: 'web',
    name: citation.title?.trim() || publisher || hostname || citation.url,
    shortName: hostname ?? publisher ?? citation.url,
    hostname,
    location: location || null,
    quote,
    image: imageSrc ? { kind: 'url', src: imageSrc } : null,
    verification: 'matched',
    onOpen: () => {
      window.open(citation.url, '_blank', 'noopener,noreferrer')
    },
  }
}

/** The drawer's Web group: one row per url (favicon, domain, title, snippet when present). */
export function ThreadWebSourcesList(props: ThreadWebSourcesListProps) {
  const { citations } = props

  const { t } = useTranslation('app')

  const groups = groupWebCitationsByUrl(citations).filter((group) => Boolean(group.citations[0]?.url))

  if (groups.length === 0) {
    return null
  }

  return (
    <div className="space-y-1 px-4 pb-4 pt-2" data-testid="thread-web-sources-list">
      <Heading level={2} size="sm" weight="medium">
        {t('threads.detail.rail.webSources')}
      </Heading>
      <ul className="-mx-2 space-y-0.5">
        {groups.map((group, index) => (
          <li key={group.key}>
            <SourceRow source={webCitationSource(group.citations[0], index)} testId={`thread-web-source-row-${index}`} />
          </li>
        ))}
      </ul>
    </div>
  )
}

/** How many url groups the Web list shows, for the Context count. */
export function webSourceGroupCount(citations: Array<WebCitation> | undefined): number {
  return groupWebCitationsByUrl(citations ?? []).filter((group) => Boolean(group.citations[0]?.url)).length
}
